/// International maritime semaphore alphabet.
///
/// Each letter is encoded as two arm positions. We use the standard
/// "8-point clock" position numbering — every 45° around a full
/// circle, viewed from in front of the sender. Numbers run 1–8
/// clockwise starting from 12 o'clock (straight up):
///
///   1 = up         (12 o'clock)
///   2 = up-right   (1:30)
///   3 = right      (3:00, horizontal)
///   4 = down-right (4:30)
///   5 = down       (6:00)
///   6 = down-left  (7:30)
///   7 = left       (9:00, horizontal)
///   8 = up-left    (10:30)
///
/// `right` / `left` here are from the SENDER's perspective. When
/// viewing the sender from the front, the sender's right arm
/// appears on the viewer's left and vice versa. The SVG renderer
/// flips this for natural viewing.
///
/// Each letter is `{ right, left }` — the arm position number for
/// each arm. The encoding follows the conventional alphabet used in
/// scouting and maritime training; sources occasionally disagree
/// on a small handful of letters (J, V, Y, Z in particular), but
/// the table here gives 26 unique combinations — verified by the
/// `code.test.ts` uniqueness assertion — so an `encode → decode`
/// round trip is loss-free.

export type ArmPosition = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export interface FlagPose {
  /// Sender's right arm position.
  right: ArmPosition;
  /// Sender's left arm position.
  left: ArmPosition;
}

/// Convert a clock-face position number to an angle in degrees,
/// measured clockwise from 12 o'clock. Used by SVG rendering to lay
/// out the arms.
export function positionToDegrees(position: ArmPosition): number {
  return (position - 1) * 45;
}

/// Letters A–Z. Source: standard international maritime semaphore
/// flag chart (the one Scout / Sea Cadet / RYA literature reproduces).
export const SEMAPHORE_CODE: Readonly<Record<string, FlagPose>> = Object.freeze({
  A: { right: 4, left: 5 },
  B: { right: 3, left: 5 },
  C: { right: 2, left: 5 },
  D: { right: 1, left: 5 },
  E: { right: 5, left: 8 },
  F: { right: 5, left: 7 },
  G: { right: 5, left: 6 },
  H: { right: 3, left: 6 },
  I: { right: 2, left: 6 },
  J: { right: 1, left: 7 },
  K: { right: 4, left: 1 },
  L: { right: 4, left: 8 },
  M: { right: 4, left: 7 },
  N: { right: 4, left: 6 },
  O: { right: 3, left: 8 },
  P: { right: 3, left: 1 },
  Q: { right: 2, left: 1 },
  R: { right: 2, left: 8 },
  S: { right: 2, left: 7 },
  T: { right: 1, left: 8 },
  U: { right: 8, left: 7 },
  V: { right: 1, left: 4 },
  W: { right: 8, left: 6 },
  X: { right: 8, left: 5 },
  Y: { right: 3, left: 2 },
  Z: { right: 4, left: 3 },
});

/// Reverse lookup. Built once; lookup by stringified pose.
export const SEMAPHORE_DECODE: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(
    Object.entries(SEMAPHORE_CODE).map(([letter, pose]) => [
      poseKey(pose),
      letter,
    ]),
  ),
);

export function poseKey(pose: FlagPose): string {
  return `${pose.right}-${pose.left}`;
}

/// Numerical signal mechanic. To send digits over semaphore the
/// transmitter sends the "numbers follow" prefix pose (J), then
/// each digit is signalled with a letter pose A–K mapped one-for-one
/// to 1–9, 0. After the number sequence the transmitter sends the
/// "letters follow" pose to return to alphabet mode.
///
/// Source: International Code of Signals + Royal Navy Communicator
/// Branch handbook (BR 67 §6).

/// Pose to flash before any digit sequence — signals "the next
/// posed letter(s) are digits". Same as letter J.
export const NUMERIC_PREFIX_POSE: FlagPose = SEMAPHORE_CODE.J!;

/// Mapping digit → letter whose pose carries that digit's meaning
/// after the numeric prefix. Note K = 0 (J is the prefix marker
/// itself, so it can't double as a digit).
export const SEMAPHORE_NUMERALS: Readonly<Record<string, string>> = Object.freeze({
  "1": "A",
  "2": "B",
  "3": "C",
  "4": "D",
  "5": "E",
  "6": "F",
  "7": "G",
  "8": "H",
  "9": "I",
  "0": "K",
});

/// Reverse: the letter that's currently being read in number mode
/// → the digit it represents. Useful for building decoder tests.
export const SEMAPHORE_NUMERAL_DECODE: Readonly<Record<string, string>> =
  Object.freeze(
    Object.fromEntries(
      Object.entries(SEMAPHORE_NUMERALS).map(([digit, letter]) => [letter, digit]),
    ),
  );
